import { Box } from "@mui/material"
import type { FC } from "react"
import React, { useMemo } from "react"
import { useFormContext } from "react-hook-form"

import { FormSelect } from "@/components/UiKit"
import type { IncotermsDefinitionMap, Location } from "@/graphql/codegen/graphql"
import { useLookupWarehouses } from "@/hooks/useLookupWarehouses"
import { useCustomersLookup } from "@/screens/Pricing/hooks/useCustomerLookup"
import { mapLookupItemsToDropdownOptions } from "@/screens/Pricing/utils/lookup"
import { AddressFormat } from "@/types/common"
import { isActiveIncotermsLocation } from "@/utils/locations"
import { formatLocationAddress } from "@/utils/utils"

interface AddSpecialPriceFormProps {
  locations: Location[]
  incotermsData: IncotermsDefinitionMap[]
}

export const AddSpecialPriceForm: FC<AddSpecialPriceFormProps> = ({ locations, incotermsData }) => {
  const { control } = useFormContext()

  const { warehouses } = useLookupWarehouses()
  const { customers } = useCustomersLookup({ companyIds: undefined })

  const incotermsLocationOptions = useMemo(
    () =>
      locations.filter(isActiveIncotermsLocation).map((location) => ({
        label: formatLocationAddress(location, AddressFormat.LocationAndAddress),
        value: location.locationId,
      })),
    [locations]
  )
  const incotermsOptions = useMemo(
    () =>
      incotermsData
        .filter((incoterm): incoterm is IncotermsDefinitionMap => Boolean(incoterm.key))
        .map((incoterm) => ({ label: incoterm.key as string, value: incoterm.key as string })),
    [incotermsData]
  )
  return (
    <form>
      <Box className='flex flex-col gap-3'>
        <FormSelect
          control={control}
          name='customerId'
          label='Customer'
          placeholder='Select Customer'
          options={mapLookupItemsToDropdownOptions(customers)}
          data-testid='pricing.add-special-price.customer-field'
        />

        <FormSelect
          control={control}
          name='shipFrom'
          label='Ship From'
          placeholder='Select Ship From'
          options={mapLookupItemsToDropdownOptions(warehouses)}
          data-testid='pricing.add-special-price.ship-from-field'
        />

        <FormSelect
          control={control}
          name='incoterms'
          label='Incoterms'
          placeholder='Select Incoterms'
          options={incotermsOptions}
          data-testid='pricing.add-special-price.incoterms-field'
        />

        <FormSelect
          control={control}
          name='incotermsLocation'
          label='Incoterms Location'
          placeholder='Select Incoterms Location'
          options={incotermsLocationOptions}
          data-testid='pricing.add-special-price.incoterms-location-field'
        />
      </Box>
    </form>
  )
}
